// frontend/packages/business-console/src/components/content/SettingsTabs.jsx

import React, { useState } from "react";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import FrontmatterField from "./FrontmatterField";
import HelpLink from "../help/HelpLink";

const TAB_HELP_TOPICS = {
  brand: "change-logo",
  contact: "update-contact",
  features: "settings-overview",
  marketing: "settings-overview",
};

const TAB_ORDER = [
  "brand",
  "design",
  "identity",
  "contact",
  "business",
  "navigation",
  "social",
  "seo",
  "marketing",
  "forms",
  "reviews",
  "legal",
  "features",
  "announcement",
  "template",
];

const TAB_LABELS = {
  brand: "Brand",
  design: "Design",
  identity: "Identity",
  contact: "Contact",
  business: "Business Info",
  navigation: "Menus",
  social: "Social Media",
  seo: "Search Engines",
  marketing: "Tracking & Analytics",
  forms: "Forms",
  reviews: "Reviews",
  legal: "Legal",
  features: "Features",
  announcement: "Announcement Bar",
  template: "Template",
  primaryCta: "Main Button",
};

const TAB_DESCRIPTIONS = {
  brand: "Your logo, colors, and visual identity",
  design: "Typography, corner style, and surface presets for your site",
  identity: "Business name, tagline, and description",
  contact: "Phone, email, address, and business hours",
  business: "Business type and details for search engines",
  navigation: "Header and footer menu links",
  social: "Your social media profile links",
  seo: "How your site appears in Google and other search engines",
  marketing: "Analytics and advertising tracking codes",
  forms: "Contact form and booking settings",
  reviews: "Customer review settings",
  legal: "Legal entity information",
  features: "Enable or disable site sections",
  announcement: "A site-wide banner above your header for promos or notices",
  template: "Template version and tier (managed by UpRyzze)",
};

function humanizeTab(key) {
  return (
    TAB_LABELS[key] ||
    String(key)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]/g, " ")
      .replace(/^./, (c) => c.toUpperCase())
  );
}

export default function SettingsTabs({
  schemaFields,
  frontmatterObj,
  showAllOptional,
  onChange,
  onPickMedia,
}) {
  const [activeTab, setActiveTab] = useState(0);

  if (!schemaFields) return null;

  const objectKeys = [];
  const scalarKeys = [];

  for (const key of Object.keys(schemaFields)) {
    const spec = schemaFields[key];
    if (spec?.type === "object" && spec?.fields) {
      objectKeys.push(key);
    } else {
      scalarKeys.push(key);
    }
  }

  objectKeys.sort((a, b) => {
    const ai = TAB_ORDER.indexOf(a);
    const bi = TAB_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const tabs = [];
  if (scalarKeys.length > 0) {
    tabs.push({ key: "__general", label: "General", description: "Basic site information", fields: scalarKeys });
  }
  for (const key of objectKeys) {
    tabs.push({ key, label: humanizeTab(key), description: TAB_DESCRIPTIONS[key] || "", fields: [key] });
  }

  const currentTab = tabs[activeTab] || tabs[0];

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minWidth: 80 },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {currentTab && (
        <Box>
          {(currentTab.description || TAB_HELP_TOPICS[currentTab.key]) && (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              {currentTab.description && (
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  {currentTab.description}
                </Typography>
              )}
              {TAB_HELP_TOPICS[currentTab.key] && (
                <HelpLink topic={TAB_HELP_TOPICS[currentTab.key]} />
              )}
            </Stack>
          )}
          <Stack spacing={1.25}>
            {currentTab.fields.map((fieldKey) => {
              const spec = schemaFields[fieldKey];
              if (!spec) return null;

              if (spec.type === "object" && spec.fields && currentTab.key !== "__general") {
                return Object.entries(spec.fields).map(([childKey, childSpec]) => (
                  <FrontmatterField
                    key={`${fieldKey}.${childKey}`}
                    path={`${fieldKey}.${childKey}`}
                    spec={childSpec}
                    frontmatterObj={frontmatterObj}
                    showAllOptional={showAllOptional}
                    onChange={onChange}
                    onPickMedia={onPickMedia}
                  />
                ));
              }

              return (
                <FrontmatterField
                  key={fieldKey}
                  path={fieldKey}
                  spec={spec}
                  frontmatterObj={frontmatterObj}
                  showAllOptional={showAllOptional}
                  onChange={onChange}
                  onPickMedia={onPickMedia}
                />
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
